import React, { useState, useEffect } from 'react';
import { Download, AlertTriangle } from 'lucide-react';
import * as XLSX from 'xlsx';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

const HistorialAcademicoAdmision = () => {
  const [estudiante, setEstudiante] = useState(null);
  const [asignaturas, setAsignaturas] = useState([]);
  const [notas, setNotas] = useState({});
  const [loading, setLoading] = useState(true);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [codigosInscritos, setCodigosInscritos] = useState(new Set());

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const queryId = urlParams.get('id_usuario');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const id_usuario = queryId || user.id_usuario || '';
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

      // Cargar info del estudiante de preadmisión
      const respEstudiante = await fetch(`${API_URL}/estudiante/info-preadmision?id_usuario=${id_usuario}`, { headers });

      if (respEstudiante.ok) {
        const dataEst = await respEstudiante.json();
        setEstudiante(dataEst.estudiante);
      } else {
        // Fallback con datos del localStorage
        setEstudiante({
          codigo: generarCodigoCPA(user.id_usuario || 1),
          nombres: user.nombres || user.nombre || 'Estudiante',
          apellidos: `${user.primer_apellido || user.apellido || ''} ${user.segundo_apellido || ''}`.trim(),
          cedula_identidad: user.cedula || '12345678',
          modalidad_postulacion: 'Curso de Preadmisión'
        });
      }

      // Cargar las 4 asignaturas de CPA desde la base de datos
      const respAsignaturas = await fetch(`${API_URL}/jefatura/asignaturas-preadmision?modalidad=CPA`, { headers });
      if (respAsignaturas.ok) {
        const dataAsig = await respAsignaturas.json();
        setAsignaturas(dataAsig.asignaturas || []);
      }

      // Cargar notas del estudiante (puede fallar, no es crítico)
      try {
        const respNotas = await fetch(`${API_URL}/estudiante/notas-preadmision?id_usuario=${id_usuario}`, { headers });
        if (respNotas.ok) {
          const dataNotas = await respNotas.json();
          const notasMap = {};
          (dataNotas.notas || []).forEach(n => {
            const codigo = n.codigo_asignatura || n.asignatura || n.codigo;
            if (codigo) notasMap[codigo] = n;
          });
          setNotas(notasMap);
        }
      } catch (e) {
        console.log('Notas no disponibles aún');
      }

      // 3. Obtener inscripciones reales (horario)
      const anioActual = new Date().getFullYear();
      const respInsc = await fetch(`${API_URL}/estudiante/horarios/disponibles/${anioActual}?tipo=preadmision&id_usuario=${id_usuario}`, { headers });
      if (respInsc.ok) {
        const dataInsc = await respInsc.json();
        const seleccion = dataInsc.horarioSeleccionado || [];
        const todosHorarios = dataInsc.horarios || [];
        const codes = new Set();

        seleccion.forEach(item => {
          const id = typeof item === 'object' ? String(item.id) : String(item);
          const h = todosHorarios.find(x => String(x.id) === id);
          if (h && (h.asignatura || h.nombre_asignatura)) {
            if (h.asignatura) codes.add(h.asignatura.toLowerCase().trim());
            if (h.nombre_asignatura) codes.add(h.nombre_asignatura.toLowerCase().trim());
          }
        });
        setCodigosInscritos(codes);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Generar código C.P.A.-XXX-YY
  const generarCodigoCPA = (id) => {
    const numero = String(id).padStart(3, '0');
    const gestion = String(new Date().getFullYear()).slice(-2);
    return `C.P.A.-${numero}-${gestion}`;
  };

  // Filtrar asignaturas para mostrar inscritas O calificadas
  const asignaturasFiltradas = asignaturas.filter(a => {
    const estaEnNotas = notas[a.codigo] !== undefined || notas[a.nombre] !== undefined;
    const estaInscrito = codigosInscritos.has(a.codigo?.toLowerCase().trim()) ||
      codigosInscritos.has(a.nombre?.toLowerCase().trim());
    return estaEnNotas || estaInscrito;
  });

  // Calcular promedio general basado solo en las filtradas
const calcularPromedioGeneral = () => {
    // Obtenemos todos los promedios de las materias filtradas
    const notasConPromedio = asignaturasFiltradas
      .map(a => notas[a.codigo]?.promedio)
      .filter(p => p !== undefined && p !== null && p !== '');

    // Si no hay ninguna nota registrada, devolvemos 0
    if (notasConPromedio.length === 0) return 0;
    
    const suma = notasConPromedio.reduce((acc, p) => acc + parseFloat(p), 0);
    
    // Retornamos el promedio redondeado al entero más cercano
    return Math.round(suma / notasConPromedio.length);
  };

  const promedioGeneral = calcularPromedioGeneral();
  // El estudiante tiene todas las notas si las asignaturas filtradas tienen promedio
  const tieneTodasLasNotas = asignaturasFiltradas.length > 0 && 
    asignaturasFiltradas.every(a => 
      notas[a.codigo]?.promedio !== undefined && 
      notas[a.codigo]?.promedio !== null && 
      notas[a.codigo]?.promedio !== ''
    );
  // Solo está habilitado si tiene TODAS las notas y promedio >= 61
  const estaHabilitado = tieneTodasLasNotas && promedioGeneral >= 61;

  // Exportar a Excel
  const exportToExcel = () => {
    const dataExport = asignaturasFiltradas.map((asig, i) => {
      const nota = notas[asig.codigo] || {};
      return {
        'No': i + 1,
        'Código': asig.codigo,
        'Asignatura': asig.nombre,
        'Nota': nota.promedio || '-'
      };
    });

    dataExport.push({
      'No': '',
      'Código': '',
      'Asignatura': 'Nota General',
      'Nota': tieneTodasLasNotas ? promedioGeneral : '-'
    });

    // Crear hoja con datos empezando en fila 4
    const ws = XLSX.utils.json_to_sheet(dataExport, { origin: 'A4' });

    // Añadir Nota en A1
    XLSX.utils.sheet_add_aoa(ws, [[
      'Nota: El siguiente boletín se presenta únicamente con fines informativos y no tiene validez curricular.'
    ]], { origin: 'A1' });

    // Añadir Título en A2
    XLSX.utils.sheet_add_aoa(ws, [[`BOLETÍN ACADÉMICO - ${estudiante?.modalidad_postulacion || 'PREADMISIÓN'}`]], { origin: 'A2' });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Boletín Académico');
    XLSX.writeFile(wb, `boletin_academico_${estudiante?.codigo || 'estudiante'}.xlsx`);
  };

  if (loading) {
    return (
      <div className="p-6 flex justify-center items-center">
        <div className="animate-spin mr-2">⏳</div> Cargando historial académico...
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-800">Estudiante &gt; Historial Academico</h1>
      </div>

      {/* Información del Estudiante */}
      <div className="flex flex-wrap gap-8 mb-6 items-start">
        {/* Botón Descargar - A la izquierda */}
        <div>
          <button
            onClick={() => setShowConfirmModal(true)}
            className="p-2 bg-blue-900 text-white rounded-full hover:bg-blue-800 transition-colors"
            title="Descargar Boletín"
          >
            <Download size={20} />
          </button>
        </div>
        <div>
          <p className="text-sm text-gray-600 font-medium">Codigo:</p>
          <p className="font-semibold text-gray-800">{estudiante?.codigo || '-'}</p>
        </div>
        <div>
          <p className="text-sm text-gray-600 font-medium">Nombres y Apellidos:</p>
          <p className="font-semibold text-gray-800">
            {estudiante?.nombres} {estudiante?.apellidos}
          </p>
        </div>
        <div>
          <p className="text-sm text-gray-600 font-medium">Cedula de Identidad:</p>
          <p className="font-semibold text-gray-800">{estudiante?.cedula_identidad || estudiante?.ci || '-'}</p>
        </div>
        <div>
          <p className="text-sm text-gray-600 font-medium">Modalidad Postulacion:</p>
          <p className="font-semibold text-gray-800">{estudiante?.modalidad_postulacion || 'Curso de Preadmisión'}</p>
        </div>
      </div>

      {/* Tabla de Notas - Solo muestra inscritas */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-4 py-3 text-center font-semibold border">No</th>
              <th className="px-4 py-3 text-center font-semibold border">Código</th>
              <th className="px-4 py-3 text-center font-semibold border">Asignatura</th>
              <th className="px-4 py-3 text-center font-semibold border">Nota</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {asignaturasFiltradas.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                  No se encontraron materias inscritas en este periodo.
                </td>
              </tr>
            ) : (
              asignaturasFiltradas.map((asig, idx) => {
                const nota = notas[asig.codigo] || {};
                return (
                  <tr key={asig.id_asignatura_preadmision || asig.codigo} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-center border">{idx + 1}</td>
                    <td className="px-4 py-3 text-center border font-mono text-blue-700">{asig.codigo}</td>
                    <td className="px-4 py-3 border">{asig.nombre}</td>
                    <td className="px-4 py-3 text-center border font-medium">{nota.promedio || '-'}</td>
                  </tr>
                );
              })
            )}
            {/* Fila de Nota General */}
            <tr className="bg-gray-50">
              <td colSpan={3} className="px-4 py-3 text-right font-semibold border">Nota General</td>
              <td className="px-4 py-3 text-center font-bold border text-lg">
                {tieneTodasLasNotas ? promedioGeneral : '-'}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Estado Habilitado/No Habilitado */}
      <div className="mt-6 text-center">
        <p className="text-base">
          En funcion del resultado obtenido se encuentra{' '}
          <span className={`font-bold ${estaHabilitado ? 'text-blue-600' : tieneTodasLasNotas ? 'text-red-600' : 'text-yellow-600'}`}>
            {tieneTodasLasNotas
              ? (estaHabilitado ? 'HABILITADO' : 'NO HABILITADO')
              : 'PENDIENTE'}
          </span>
        </p>
      </div>

      {/* Modal de Confirmación de Descarga */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 text-center">
            <div className="flex justify-center mb-4 text-yellow-500">
              <AlertTriangle size={48} />
            </div>
            <h3 className="text-lg font-bold text-gray-800 mb-2">Aviso Importante</h3>
            <p className="text-gray-600 mb-6 text-sm">
              Nota: El siguiente boletín se presenta únicamente con fines informativos y no tiene validez curricular.
            </p>
            <div className="flex justify-center gap-3">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  exportToExcel();
                  setShowConfirmModal(false);
                }}
                className="px-4 py-2 bg-blue-900 text-white rounded-lg hover:bg-blue-800"
              >
                Aceptar y Descargar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HistorialAcademicoAdmision;